import React, { useContext, useEffect, useState } from 'react';
import { OwcButton, OwcIcon, OwcListItem } from '@one/react';
import { GraphQLResult } from '@aws-amplify/api-graphql';
import { configureAwsAppSync } from '../../../utils/helpers/configureAwsAppSync';
import { formStates, REPO_CONFIG } from '../../../constants';
import { LIST_IR_EDITREASON_MASTER } from '../../../gql/logBooksapi';
import { API } from 'aws-amplify';
import { CreateLogSheetChange } from "../log-sheet/log-sheet-form/CreateLogSheetChange";
import { withApollo } from 'react-apollo';
import { emptyLogSheet } from '../log-sheet/log-sheet-form/empty-log-sheet';
import { makeCleanLogSheet } from '../log-sheet/log-sheet-form/make-clean-log-sheet';
import { useSelector } from 'react-redux';
import { CoverSheetMainPageContext } from '../../cover-sheet/cover-sheet-main-page-context/context';
import { PaginationContext } from '../../../components/shared/pagination/PaginationContext';
import { DialogServiceContext } from '../../../utils/hooks/dialogService/DialogServiceContext';
import { reasonDialogColumnsWithConditionallyRequiredComment } from '../../../utils/constants/reasonDialogColumns';
import { DigitalLabLogbookInstrumentModelType } from '../../../models/DigitalLabLogbookInstrumentModelType';

type reasonsResponseType = {
  listIRConfigurableMasters: {
    items: {
      configText: string;
    }[],
  },
};

type ApproveActionLogProps = {
  isMenuItem?: boolean;
  label?: string;
  item: DigitalLabLogbookInstrumentModelType;
  client: unknown;
};

type UserType = {
  user: string;
}

const prepareCleanDataForApproval = (item: DigitalLabLogbookInstrumentModelType, user: UserType) => {
  const cleanItem = makeCleanLogSheet({
    item,
    isNew: false,
    user,
    formState: formStates.EDITABLE_FORM
  })
  const initialValues = {
    ...emptyLogSheet,
    ...cleanItem,
    action: cleanItem.action.key
  };

  return initialValues
}

const ApproveActionLog = ({
  isMenuItem = false,
  label,
  client,
  item
}: ApproveActionLogProps) => {
  const [reasonList, setReasonList] = useState<string[]>([]);
  const dialogService = useContext(DialogServiceContext);
  const isLogApproved = item?.isApproved;
  const {
    refreshTable
  } = useContext(CoverSheetMainPageContext);
  const { dispatchAction } = useContext(PaginationContext) as { dispatchAction: (action: { type: string }) => void };
  const user = useSelector((state: { user: UserType }) => state.user);

  const onOpen = () => (
    dialogService.show({
      title: "Reason for approving",
      type: "form",
      initialValues: {},
      columns: reasonDialogColumnsWithConditionallyRequiredComment(reasonList),
      confirmText: "Save",
    }) as Promise<Record<string, string> | boolean>
  ).then(async (selectedValues) => {
    const { reason, comment } = selectedValues as { reason: string; comment: string };
    const values = prepareCleanDataForApproval(item, user);
    const response = await CreateLogSheetChange(
      {
        ...values,
        editReason: reason,
        editComment: comment
      },
      {
        client,
        isNew: false,
        isProcessingApproval: true,
        equipmentDetail: item
      }
    );

    if (response.error) {
      console.log('Approving action log failed. Error:', response.error);
      return;
    } else {
      refreshTable();
      dispatchAction({
        type: "clearAll"
      });
      console.log('Save approved action log.', response);
    }
  }).catch(() => {
    console.log('Action log approval has been cancelled.');
  });

  const getEditReasons = async () => {
    configureAwsAppSync(REPO_CONFIG);
    try {
      const result = await API.graphql({
        query: LIST_IR_EDITREASON_MASTER,
        variables: {
          limit: 1000,
          nextToken: null
        }
      }) as GraphQLResult<reasonsResponseType>;

      return result.data?.listIRConfigurableMasters.items;
    } catch (err: any) {
      console.error('Error fetching reason list: ', err.message);
      return [];
    }
  };

  useEffect(() => {
    const fetchEditReasons = async () => {
      const editReasons = await getEditReasons();
      if (editReasons && !editReasons.hasOwnProperty('error')) {
        const transformedReasons = editReasons.map((reason) => reason.configText);
        setReasonList(transformedReasons);
      }
    };
    fetchEditReasons();
  }, []);

  if (isMenuItem) {
    return (
      <OwcListItem
        onClick={onOpen}
        id={"approve"}
        disabled={isLogApproved}
      >
        Approve
      </OwcListItem>
    )
  }

  if (isLogApproved) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: '5px' }}>
        <OwcIcon name="certificate" family="outlined" ></OwcIcon>
        <strong>Approved</strong>
      </div>
    )
  }

  return (
    <>
      <div data-testid="details-cell-label">{label}</div>
      <div style={{ padding: "3px" }}>
        <OwcButton data-testid="approve-action-log-button" variant="secondary" onClick={onOpen}>
          Approve
        </OwcButton>
      </div>
    </>
  )
}

export default withApollo(ApproveActionLog)
